import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Container, Stack, Typography } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { CalendarDays, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import EventCard from '../components/events/EventCard';
import EventInvitationCard from '../components/events/EventInvitationCard';
import EventDetailsDialog from '../components/events/EventDetailsDialog';
import EventTaskLinksDialog from '../components/events/EventTaskLinksDialog';
import { LoadingIndicator } from '../components/assets';
import { deleteEvent, getEventParticipants, getEvents, getEventTaskLinks } from '../utils/api';

const EVENT_HISTORY_WINDOW_MS = 24 * 60 * 60 * 1000;

export const eventIsVisible = (event, now = Date.now()) => (
  new Date(event.ends_at).getTime() >= now - EVENT_HISTORY_WINDOW_MS
);

export default function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [taskEvent, setTaskEvent] = useState(null);
  const [taskLinks, setTaskLinks] = useState([]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const [loadedEvents, loadedInvitations, loadedTaskLinks] = await Promise.all([
        getEvents(),
        getEventParticipants({ status: 'invited' }),
        getEventTaskLinks(),
      ]);
      setEvents(loadedEvents.filter((event) => eventIsVisible(event)));
      setInvitations(loadedInvitations.filter(
        (invitation) => eventIsVisible(invitation.event),
      ));
      setTaskLinks(loadedTaskLinks);
    } catch (loadError) {
      setError(loadError.data?.message || loadError.message || 'Unable to load events.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const refresh = () => load({ quiet: true });
    window.addEventListener('assistantUpdate', refresh);
    return () => window.removeEventListener('assistantUpdate', refresh);
  }, [load]);

  const linksForEvent = useCallback(
    (eventId) => taskLinks.filter((link) => link.event_id === eventId),
    [taskLinks],
  );

  const handleLinksChange = useCallback((eventId, updatedLinks) => {
    setTaskLinks((current) => [
      ...current.filter((link) => link.event_id !== eventId),
      ...updatedLinks,
    ]);
  }, []);

  const remove = async (event) => {
    if (!window.confirm(`Cancel “${event.title}”? Attendees will be notified.`)) return;
    setDeletingId(event.id);
    setError('');
    try {
      await deleteEvent(event.id, event.revision);
      setEvents((current) => current.filter((item) => item.id !== event.id));
    } catch (deleteError) {
      setError(deleteError.data?.message || deleteError.message || 'Unable to cancel the event.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3, pb: 18 }}>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" sx={{ color: 'primary.main' }}>Events</Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'secondary.main', textAlign: 'left' }}>
              Keep important dates and places together.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => navigate('/event')}
            sx={(theme) => ({
              m: 0,
              color: theme.palette.background.default,
              bgcolor: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              boxShadow: `0 6px 0 ${darken(theme.palette.primary.main, 0.58)}`,
              '&:hover': {
                color: theme.palette.background.default,
                bgcolor: theme.palette.primary.main,
                transform: 'translate(-2px, -2px)',
                boxShadow: `2px 8px 0 ${darken(theme.palette.primary.main, 0.58)}`,
              },
              '&:active': { transform: 'translateY(4px)', boxShadow: 'none' },
            })}
          >
            Add event
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ color: 'secondary.main' }}>{error}</Alert>}
        {loading ? <LoadingIndicator text="loading events" /> : (
          <Stack spacing={2.5}>
            {invitations.length > 0 && (
              <Stack spacing={1.5}>
                <Typography variant="h6" sx={{ color: 'secondary.main' }}>
                  Invitations
                </Typography>
                {invitations.map((invitation) => (
                  <EventInvitationCard
                    key={invitation.id}
                    invitation={invitation}
                    onResolved={load}
                  />
                ))}
              </Stack>
            )}
            {!events.length && !invitations.length && (
              <Box
                sx={(theme) => ({
                  p: { xs: 3, sm: 5 },
                  textAlign: 'center',
                  border: `3px solid ${theme.palette.green.main}`,
                  borderRadius: 5,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: `0 0 30px ${alpha(theme.palette.green.main, 0.2)}`,
                })}
              >
                <CalendarDays size={42} color="#39FF14" />
                <Typography variant="h6" sx={{ mt: 2, color: 'primary.main' }}>
                  No events yet
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: 'secondary.main' }}>
                  Add an event when you have a date you want to keep visible.
                </Typography>
              </Box>
            )}
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                taskLinks={linksForEvent(event.id)}
                deleting={deletingId === event.id}
                onEdit={() => navigate(`/event/${event.id}`)}
                onOpen={() => setSelectedEvent(event)}
                onDelete={remove}
                onManageTasks={() => setTaskEvent({ event, initialTravelOpen: false })}
                onAddTravel={() => setTaskEvent({ event, initialTravelOpen: true })}
              />
            ))}
          </Stack>
        )}
      </Stack>
      <EventDetailsDialog
        event={selectedEvent}
        taskLinks={selectedEvent ? linksForEvent(selectedEvent.id) : []}
        open={Boolean(selectedEvent)}
        onClose={() => setSelectedEvent(null)}
        onEdit={() => navigate(`/event/${selectedEvent.id}`)}
        onLeft={() => {
          setSelectedEvent(null);
          load();
        }}
      />
      <EventTaskLinksDialog
        event={taskEvent?.event}
        open={Boolean(taskEvent)}
        onClose={() => setTaskEvent(null)}
        onLinksChange={handleLinksChange}
        initialTravelOpen={taskEvent?.initialTravelOpen}
      />
    </Container>
  );
}
